import axios, { type AxiosError } from 'axios';

import { setClientProfileId } from '@/config/axios';
import { notifyRouteServerError } from '@/config/routeServerError';
import { IS_ADMIN_KEY } from '@/constants/roles';
import {
  ADMIN_TOKEN_KEY,
  removeAdminUserId,
  removeLocalStorageItem,
} from '@/utils/localStorage';

let initialized = false;
let onAuthError: ((error: AxiosError) => void) | null = null;

export function setAuthErrorHandler(
  handler: ((error: AxiosError) => void) | null,
) {
  onAuthError = handler;
}

function handleUnauthorized() {
  const path = window.location.pathname;
  if (path.startsWith('/embed/') || path.startsWith('/guest')) {
    return;
  }

  removeLocalStorageItem('access_token');
  removeLocalStorageItem('access_token_type');
  removeLocalStorageItem(IS_ADMIN_KEY);
  removeLocalStorageItem(ADMIN_TOKEN_KEY);
  removeAdminUserId();
  setClientProfileId(null);

  window.location.href = '/';
}

function hadAuthorizationHeader(error: AxiosError): boolean {
  const headers = error.config?.headers;
  if (!headers || typeof headers !== 'object') return false;
  const headerRecord = headers as Record<string, unknown>;
  const auth = headerRecord.Authorization ?? headerRecord.authorization;
  return typeof auth === 'string' && auth.length > 0;
}

export function initApiClient() {
  if (initialized) return;
  initialized = true;

  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401 || status === 403) {
        onAuthError?.(error);
        if (status === 401 && hadAuthorizationHeader(error)) {
          handleUnauthorized();
        }
      } else if (error.response && status !== undefined && status >= 500) {
        if (!error.config?.skipGlobalServerError) {
          notifyRouteServerError();
        }
      }
      return Promise.reject(error);
    },
  );
}
